import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IllustListMasters, IllustListSearchConditions } from '../../../../api/model';
import yup from '../../../../utils/custom-yup';
import MultiSelect from '../../../atoms/MultiSelect';
import NoDataComponent from '../../../atoms/NoDataComponent';

interface Props {
  masters: IllustListMasters;
  defaultValues: IllustListSearchConditions;
  onSubmit: SubmitHandler<IllustListSearchConditions>;
}

function SearchConditions({ masters, defaultValues, onSubmit }: Props) {
  // 言語情報
  const { t } = useTranslation();

  /**
   * フォーム情報
   */
  // エラーメッセージ
  const conditionMessage = t('条件を一つ設定してください。');

  // チェック内容
  const validateFunc = (value: any, testContext: yup.TestContext) => {
    const p = testContext.parent;
    const jgValue = p.mys !== undefined && p.mys.length !== 0; // M/Yの未入力判定
    const jgChapter = p.chapter_number_ids !== undefined && p.chapter_number_ids.length !== 0; // 章の未入力判定
    const jgMainLang = p.main_language_ids !== undefined && p.main_language_ids.length !== 0; // 主言語の未入力判定
    const jgDest = p.destination_ids !== undefined && p.destination_ids.length !== 0; // 仕向の未入力判定
    const jgProNo = p.production_no !== undefined && p.production_no !== ''; // 資料管理Noの未入力判定
    const jgOtherLang = p.other_language_ids !== undefined && p.other_language_ids.length !== 0; // 他言語の未入力判定
    const jgProDiv = p.production_division_ids !== undefined && p.production_division_ids.length !== 0; // 資料区分の未入力判定
    const jgTitle = p.title !== undefined && p.title !== ''; // 資料名の未入力判定
    const jgModel = p.model_names !== undefined && p.model_names.length !== 0; // 機種名の未入力判定

    return jgValue || jgChapter || jgMainLang || jgDest || jgProNo || jgOtherLang || jgProDiv || jgTitle || jgModel;
  };

  // チェックの実装
  const schema = yup.object().shape({
    mys: yup.array().test('未入力チェック', conditionMessage, validateFunc),
    chapter_number_ids: yup.array().test('未入力チェック', conditionMessage, validateFunc),
    main_language_ids: yup.array().test('未入力チェック', conditionMessage, validateFunc),
    destination_ids: yup.array().test('未入力チェック', conditionMessage, validateFunc),
    production_no: yup.string().test('未入力チェック', conditionMessage, validateFunc),
    other_language_ids: yup.array().test('未入力チェック', conditionMessage, validateFunc),
    production_division_ids: yup.array().test('未入力チェック', conditionMessage, validateFunc),
    title: yup.string().test('未入力チェック', conditionMessage, validateFunc),
    model_names: yup.array().test('未入力チェック', conditionMessage, validateFunc),
  });

  // フォーム管理
  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm({ defaultValues, resolver: yupResolver(schema) });

  // マスタデータが不足している場合
  if (
    masters.destinations == null ||
    masters.model_names == null ||
    masters.chapter_numbers == null ||
    masters.main_languages == null ||
    masters.other_languages == null ||
    masters.production_divisions == null
  ) {
    return <NoDataComponent />;
  }

  return (
    <div className="wfr scondition trigfr fropen">
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          await handleSubmit(onSubmit)();
        }}
      >
        <div className="grid3 labip1">
          <div>
            <label htmlFor="mys">{t('M/Y')}</label>
            <MultiSelect
              id="mys"
              name="mys"
              options={[...Array(31)].map((_, i) => (i + 2020).toString())}
              control={control}
              isDisabled={false}
              getValue={(x) => x}
              getLabel={(x) => x}
            />
          </div>
          <div>
            <label htmlFor="chapter_number_ids">{t('章番号')}</label>
            <MultiSelect
              id="chapter_number_ids"
              name="chapter_number_ids"
              options={masters.chapter_numbers}
              control={control}
              isDisabled={false}
              getValue={(x) => x.chapter_number_id}
              getLabel={(x) => x.chapter_name}
            />
          </div>
          <div>
            <label htmlFor="main_language_ids">{t('主言語')}</label>
            <MultiSelect
              id="main_language_ids"
              name="main_language_ids"
              options={masters.main_languages}
              control={control}
              isDisabled={false}
              getValue={(x) => x.language_id}
              getLabel={(x) => x.language_name}
            />
          </div>
          <div>
            <label htmlFor="destination_ids">{t('仕向')}</label>
            <MultiSelect
              id="destination_ids"
              name="destination_ids"
              options={masters.destinations}
              control={control}
              isDisabled={false}
              getValue={(x) => x.destination_id}
              getLabel={(x) => x.destination_name}
            />
          </div>
          <div>
            <label htmlFor="production_no">{t('資料管理No')}</label>
            <input id="production_no" type="text" {...register('production_no')} />
          </div>
          <div>
            <label htmlFor="other_language_ids">{t('他言語')}</label>
            <MultiSelect
              id="other_language_ids"
              name="other_language_ids"
              options={masters.other_languages}
              control={control}
              isDisabled={false}
              getValue={(x) => x.language_id}
              getLabel={(x) => x.language_name}
            />
          </div>
          <div>
            <label htmlFor="production_division_ids">{t('カテゴリ')}</label>
            <MultiSelect
              id="production_division_ids"
              name="production_division_ids"
              options={masters.production_divisions}
              control={control}
              isDisabled={false}
              getValue={(x) => x.production_division_id}
              getLabel={(x) => x.production_division_name}
            />
          </div>
          <div>
            <label htmlFor="title">{t('資料名')}</label>
            <input id="title" type="text" {...register('title')} />
          </div>
          <div />
          <div>
            <label htmlFor="model_names">{t('機種名')}</label>
            <MultiSelect
              id="model_names"
              name="model_names"
              options={masters.model_names}
              control={control}
              isDisabled={false}
              getValue={(x) => x}
              getLabel={(x) => x}
            />
          </div>
        </div>
        <div className="controlfr" style={{ marginTop: '0px' }}>
          <span>
            <input type="submit" id="" name="" value={t('検索').toString()} className="searchbtn" />
            {errors.mys?.message &&
              errors.chapter_number_ids?.message &&
              errors.main_language_ids?.message &&
              errors.destination_ids?.message &&
              errors.production_no?.message &&
              errors.other_language_ids?.message &&
              errors.production_division_ids?.message &&
              errors.title?.message &&
              errors.model_names?.message && <p>{conditionMessage}</p>}
          </span>
        </div>
      </form>
    </div>
  );
}

export default SearchConditions;
